import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { apiClient, type PublicPlan } from '@/lib/api-client';

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE = 'rgba(22,12,36,0.85)';

function formatPriceBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function intervalLabel(interval: PublicPlan['interval']): string {
  return interval === 'year' ? '/ano' : '/mês';
}

/** `features` e jsonb livre: string simples OU {label, included}. */
function featureLabel(f: unknown): string | null {
  if (typeof f === 'string') return f;
  if (f && typeof f === 'object' && 'label' in f) {
    const label = (f as { label?: unknown }).label;
    return typeof label === 'string' ? label : null;
  }
  return null;
}

export function LandingPlansSection() {
  const navigate = useNavigate();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => apiClient.listPublicPlans(),
    staleTime: 5 * 60 * 1000,
  });

  // Sem planos ativos (ou ainda carregando): a secao nao aparece — evita
  // um bloco vazio/quebrado na landing publica.
  if (isLoading || !plans || plans.length === 0) {
    return null;
  }

  const sorted = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <section
      id="planos"
      style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: 1180,
        margin: '0 auto',
        padding: '16px clamp(16px, 4vw, 24px) 80px',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: CHAMPAGNE,
            marginBottom: 12,
          }}
        >
          Planos
        </p>
        <h2
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 400,
            fontSize: 'clamp(28px, 4vw, 40px)',
            color: FG,
            letterSpacing: '-0.01em',
          }}
        >
          Escolha seu plano e comece agora
        </h2>
      </div>

      <div
        className="plans-grid"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 20,
        }}
      >
        {sorted.map((plan) => (
          <PlanCard
            key={plan.code}
            plan={plan}
            onSelect={() => navigate(`/assinar/${plan.code}`)}
          />
        ))}
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  onSelect,
}: {
  plan: PublicPlan;
  onSelect: () => void;
}) {
  const features = plan.features
    .map(featureLabel)
    .filter((f): f is string => Boolean(f));

  return (
    <div
      style={{
        flex: '0 1 420px',
        background: SURFACE,
        border: `1px solid rgba(232,185,106,0.25)`,
        borderRadius: 16,
        padding: '32px 28px',
        backdropFilter: 'blur(14px)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, transform 0.2s ease',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'rgba(232,185,106,0.5)';
        el.style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'rgba(232,185,106,0.25)';
        el.style.transform = 'translateY(0)';
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 200,
          height: 200,
          background:
            'radial-gradient(ellipse at top right, rgba(232,185,106,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <h3
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 400,
          fontSize: 24,
          color: FG,
          marginBottom: 8,
        }}
      >
        {plan.name}
      </h3>

      {plan.description && (
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 13.5,
            color: MUTED,
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          {plan.description}
        </p>
      )}

      <div style={{ marginBottom: 20 }}>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 400,
            fontSize: 36,
            color: FG,
          }}
        >
          {formatPriceBRL(plan.priceCents)}
        </span>
        <span
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 14,
            color: MUTED,
            marginLeft: 4,
          }}
        >
          {intervalLabel(plan.interval)}
        </span>
      </div>

      {plan.trialDays > 0 && (
        <div
          style={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: CHAMPAGNE_LIGHT,
            background: 'rgba(232,185,106,0.12)',
            border: '1px solid rgba(232,185,106,0.28)',
            borderRadius: 999,
            padding: '4px 12px',
            marginBottom: 20,
          }}
        >
          {plan.trialDays} dias grátis
        </div>
      )}

      {features.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            marginBottom: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            flex: 1,
          }}
        >
          {features.map((f, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 13.5,
                color: MUTED,
                lineHeight: 1.5,
              }}
            >
              <Check
                size={15}
                style={{ color: CHAMPAGNE_LIGHT, flexShrink: 0, marginTop: 2 }}
              />
              {f}
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={onSelect}
        style={{
          marginTop: 'auto',
          background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${CHAMPAGNE_LIGHT} 100%)`,
          color: '#1A0F00',
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: 14.5,
          letterSpacing: '0.01em',
          border: 'none',
          borderRadius: 10,
          padding: '13px',
          cursor: 'pointer',
          width: '100%',
          boxShadow: `0 4px 24px rgba(232,185,106,0.3)`,
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform =
            'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform =
            'translateY(0)';
        }}
      >
        Assinar
      </button>
    </div>
  );
}
